import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';

export const Sidebar = ({
  children,
  onClose,
  isExpanded,
  onExpandToggle,
  width = '300px',
  expandedWidth = '75%',
  Footer,
  anchor = 'left',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Don't let the background scroll when the sidebar is open
  useEffect(() => {
    setIsVisible(true);
    document.body.classList.add('overflow-hidden');
    // Move focus into the sidebar on open (mirrors our modals' initial focus) —
    // without this, keyboard focus stays behind the overlay and can't reach it
    previousFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden');
      // Return focus to whatever opened the sidebar (mirrors our modals).
      // The opener may have unmounted in the meantime (e.g. a tab switch);
      // isConnected guards against focusing a detached element.
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  // close on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClose]);

  return (
    <>
      {/* Background Overlay */}
      <div className='fixed inset-0 bg-black bg-opacity-50 z-[58]' onClick={handleClose} />
      {/* Sidebar */}
      <div
        className={`fixed top-0 ${anchor === 'left' ? 'right-0' : 'left-0'} h-full bg-white shadow-lg z-[59] transform transition-all duration-500 ease-in-out py-2`}
        style={{
          width: isExpanded ? expandedWidth : width,
          transform: isVisible
            ? 'translateX(0)'
            : anchor === 'left'
              ? 'translateX(100%)'
              : 'translateX(-100%)',
        }}>
        <div className='flex justify-between '>
          <button
            onClick={onExpandToggle}
            className={`ms-2 text-sm text-gray-600 overflow-y-auto hover:text-gray-800 ${onExpandToggle ? '' : 'invisible'}`}>
            {!isExpanded && (
              <p className='flex items-center'>
                {isExpanded ? (
                  <ChevronRightIcon className='w-6 h-6 -me-1' />
                ) : (
                  <ChevronLeftIcon className='w-6 h-6 -me-1' />
                )}
                {isExpanded ? 'Collapse' : 'See All'}
              </p>
            )}
          </button>
          <button
            ref={closeButtonRef}
            aria-label='Close sidebar'
            className='text-gray-600 overflow-y-auto hover:text-gray-800 text-2xl p-2 rounded-lg focus-visible:bg-gray-100 focus-visible:outline-none'
            onClick={handleClose}>
            <XMarkIcon className='w-6 h-6' />
          </button>
        </div>
        <div
          className='relative overflow-y-auto pb-2 mb-2 px-4 '
          style={{ height: Footer ? `calc(100vh - 125px)` : `97%` }}>
          {children}
        </div>
        {Footer && (
          <div className='sticky bottom-0 bg-white'>
            <div className='mt-1 border-t border-gray-200 py-3 px-4 '>{Footer}</div>
          </div>
        )}
      </div>
    </>
  );
};
